import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Button, Input, Space, Modal, Form, Select, message } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, PlusCircleOutlined } from '@ant-design/icons';
import axios from 'axios';
import { ILink } from '@drone-parts.garphild.pro/interfaces';

type TableColumn = {
  title: string;
  dataIndex?: string;
  key: string;
  render?: (text: string | string[] | undefined, record: ILink) => React.ReactNode;
};

interface LinkFormValues {
  title: string;
  link: string;
  category: string;
  description: string;
  image?: string;
}

const { Option } = Select;

const LinksPage: React.FC = () => {
  const [links, setLinks] = useState<ILink[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingLink, setEditingLink] = useState<ILink | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [searchText, setSearchText] = useState('');
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [form] = Form.useForm<LinkFormValues>();

  const fetchLinks = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get<{ data: ILink[] }>('/api/links', {
        params: { search: searchText || undefined }
      });
      setLinks(data.data);
    } catch (error) {
      message.error('Failed to fetch links');
    } finally {
      setLoading(false);
    }
  }, [searchText]);

  const fetchCategories = async () => {
    try {
      const { data } = await axios.get('/api/links/categories');
      setCategories(data);
    } catch (error) {
      message.error('Failed to fetch categories');
    }
  };

  useEffect(() => {
    fetchLinks();
    fetchCategories();
  }, [fetchLinks]);

  const handleSubmit = async (values: LinkFormValues) => {
    try {
      const linkData = {
        ...values,
        category: [values.category] // Convert to array to match the backend model
      };
      
      if (editingLink) {
        await axios.put(`/api/links/${editingLink._id}`, linkData);
        message.success('Link updated successfully');
      } else {
        await axios.post('/api/links', linkData);
        message.success('Link created successfully');
      }
      form.resetFields();
      setIsModalVisible(false);
      setEditingLink(null);
      fetchLinks();
    } catch (error) {
      message.error('Operation failed');
    }
  };

  const handleEdit = (link: ILink) => {
    setEditingLink(link);
    form.setFieldsValue({
      title: link.title,
      link: link.link,
      description: link.description,
      category: Array.isArray(link.category) ? link.category[0] || '' : link.category || '',
      image: link.image
    });
    setIsModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await axios.delete(`/api/links/${id}`);
      message.success('Link deleted successfully');
      await fetchLinks();
    } catch (error) {
      message.error('Failed to delete link');
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedRowKeys.length) {
      message.warning('Please select at least one link to delete');
      return;
    }

    try {
      await Promise.all(selectedRowKeys.map((id) => 
        axios.delete(`/api/links/${id}`)
      ));
      message.success(`${selectedRowKeys.length} links deleted successfully`);
      setSelectedRowKeys([]);
      await fetchLinks();
    } catch (error) {
      message.error('Failed to delete selected links');
    }
  };

  const handleAddNewCategory = (e?: React.MouseEvent | React.KeyboardEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    
    const trimmedCategory = newCategory.trim();
    if (!trimmedCategory) {
      message.warning('Please enter a category name');
      return;
    }

    if (categories.includes(trimmedCategory)) {
      message.warning('This category already exists');
      return;
    }

    setCategories([...categories, trimmedCategory]);
    form.setFieldsValue({ category: trimmedCategory });
    setNewCategory('');
    setShowNewCategoryInput(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleAddNewCategory(e);
    }
  };

  const columns: TableColumn[] = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (text: string | string[] | undefined, record: ILink) => (
        <a href={record.link} target="_blank" rel="noopener noreferrer">
          {typeof text === 'string' ? text : record.title}
        </a>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (categories: string | string[] | undefined) => 
        Array.isArray(categories) ? categories.join(', ') : categories || '',
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Domain',
      dataIndex: 'domain',
      key: 'domain',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (text: string | string[] | undefined, record: ILink) => (
        <Space size="middle">
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            style={{ marginRight: 8 }}
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record._id)}
          />
        </Space>
      ),
    },
  ];

  return (
    <div className="p-4">
      <Card
        title={
          <div className="flex justify-between items-center">
            <span>Helpful Links</span>
            <Space>
              {selectedRowKeys.length > 0 && (
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={handleBulkDelete}
                  loading={loading}
                >
                  Delete Selected ({selectedRowKeys.length})
                </Button>
              )}
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setEditingLink(null);
                  form.resetFields();
                  setIsModalVisible(true);
                }}
              >
                Add Link
              </Button>
            </Space>
          </div>
        }
      >
        <div style={{ marginBottom: 16 }}>
          <Space>
            <Input
              placeholder="Search links..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 200 }}
            />
          </Space>
        </div>

        <Table<ILink>
          columns={columns}
          dataSource={links}
          rowKey="_id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          rowSelection={{
            selectedRowKeys,
            onChange: (selectedKeys: React.Key[]) => setSelectedRowKeys(selectedKeys as string[])
          }}
        />
      </Card>

      <Modal
        title={editingLink ? 'Edit Link' : 'Add New Link'}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        width={600}
      >
        <Form<LinkFormValues>
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ category: '', title: '', link: '', description: '' }}
        >
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: 'Please enter a title' }]}
          >
            <Input placeholder="Enter title" />
          </Form.Item>

          <Form.Item
            name="link"
            label="URL"
            rules={[
              { required: true, message: 'Please enter a URL' },
              { type: 'url', message: 'Please enter a valid URL' },
            ]}
          >
            <Input placeholder="https://example.com" />
          </Form.Item>


          <Form.Item
            name="category"
            label="Category"
            rules={[{ required: true, message: 'Please select a category' }]}
          >
            <div style={{ display: 'flex', gap: '8px' }}>
              <Select 
                placeholder="Select a category" 
                showSearch
                style={{ flex: 1 }}
                onChange={(value) => form.setFieldsValue({ category: value })}
                dropdownRender={(menu) => (
                  <div onClick={(e) => e.stopPropagation()}>
                    <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                      {menu}
                    </div>
                    <div style={{ padding: '8px', display: 'flex', gap: '8px', borderTop: '1px solid #f0f0f0' }}>
                      <Input
                        size="small"
                        placeholder="New category name"
                        value={newCategory}
                        onChange={(e) => setNewCategory(e.target.value)}
                        onKeyDown={handleKeyDown}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <Button 
                        type="text" 
                        size="small" 
                        icon={<PlusCircleOutlined />} 
                        onClick={handleAddNewCategory}
                      />
                    </div>
                  </div>
                )}
              >
                {categories.map((category) => (
                  <Option key={category} value={category}>
                    {category}
                  </Option>
                ))}
              </Select>
              <Button 
                type="dashed" 
                icon={<PlusOutlined />} 
                onClick={() => setShowNewCategoryInput(true)}
                style={{ display: showNewCategoryInput ? 'none' : 'block' }}
              >
                New
              </Button>
            </div>
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Please enter a description' }]}
          >
            <Input.TextArea rows={4} placeholder="Enter a brief description" />
          </Form.Item>

          <Form.Item
            name="image"
            label="Image URL (Optional)"
            rules={[{ type: 'url', message: 'Please enter a valid URL' }]}
          >
            <Input placeholder="https://example.com/image.jpg" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default LinksPage;
